import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
// import {csv} from "papaparse";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Iconify from "../../../../minimals/components/iconify";
import { UserContext } from "../../../contexts/UserContext";
import { exportTableToCsv } from "../../../taskUtils";
import { InternalUser } from "../../../types/app";
import { DashboardReportType } from "../../../types/reports";
import { NotesColumn } from "../../NotesColumn";
import { fetchPolicyInfoByOrderNumber } from "../utils/policiesToSendUtils";
import { getColumns } from "./columns";
import NewPolicyModal from "./NewPolicyModal";
import NotesAndIssuesModal from "./NotesAndIssuesModal";
import PolicyIssuesActions from "./PolicyIssuesActions";
import { PolicyIssuesContext } from "./PolicyIssuesContext";
import { Policy, PolicyIssue } from "./types";
import UploadModal from "./UploadModal";

export default function PolicyIssuesDashboard() {
  const {
    policies,
    loading,
    setDateRange,
    dateRange,
    assignedToMe,
    setAssignedToMe,
    fetchPolicies,
    setPolicies,
    tableRef,
    isDefaultFilter,
    unsentOnly,
    setIsDefaultFilter,
    setUnsentOnly
  } = useContext(PolicyIssuesContext);
  const { user } = useContext(UserContext);
  const [managerDepartments, setManagerDepartments] = useState<string[]>([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [selectedUploadPolicy, setSelectedUploadPolicy] = useState<Policy | null>(null);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openNewPolicyModal, setOpenNewPolicyModal] = useState(false);
  const [openNotesIssuesModal, setOpenNotesIssuesModal] = useState(false);
  const [openEditUpload, setOpenEditUpload] = useState(false);
  const [isConfirmEditAndDelete, setIsConfirmEditAndDelete] = useState(false);
  const [tableData, setTableData] = useState<Policy[]>([]);
  const [rowToDelete, setRowToDelete] = useState<Policy | null>(null);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [policyNumberCheck, setPolicyNumberCheck] = useState("");
  const [searchParams] = useSearchParams();
  const orderNumberFromUrl = searchParams.get("orderNumber");
  const [assignableInternalUserIds, setAssignableInternalUserIds] = useState<number[]>([]);

  const [columnFilters, setColumnFilters] = useState(
    orderNumberFromUrl
      ? [{ id: "orderNumber", value: orderNumberFromUrl }]
      : []
  );
  const hasAssignedTo = (policy: Policy) => Boolean(policy.assignedToUser || policy.assignedToName || policy.assignedToEmail);
  const documentInfo = {
    description: `${Number(selectedUploadPolicy?.type) === 2 ? "Loan " : "Owners "}${
      selectedUploadPolicy?.position ? selectedUploadPolicy?.position : ""
    } Final Policy ${
      selectedUploadPolicy?.revision && selectedUploadPolicy?.revision > 0
        ? `Revision ${selectedUploadPolicy?.revision}`
        : ""
    }`,
    base64File: "",
    fileName: "",
    subcategoryId: "PCYCOR",
    mTANumber: selectedUploadPolicy?.orderNumber,
    applicationInitiator: "TitleQ.PoliciesToSend"
  };
  const getAndSetManagerDepartmentsByEmail = async (emailAddress: string) => {
    const { data } = await axios.get<string[]>(
      `/api/outstandingchecks/getManagerDepartmentsByEmail?emailAddress=${emailAddress}`
    );
    setManagerDepartments(data);
  };

  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };
  const getAndSetAssignableInternalUserIds = async () => {
    const id = assignedToId && !isUnassignedOnly ? Number(assignedToId) : user?.id;
    if (!id) {
      setAssignableInternalUserIds([]);
      return;
    }

    const { data } = await axios.get<number[]>(
      `/api/clientphonebook/internalusers/getInternalUserIdsIncludingPairedUsers?internalUserIds=${id}`
    );
    setAssignableInternalUserIds(data);
  };

  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };
  const handleOpenModal = (selectedPolicy: Policy) => {
    setSelectedUploadPolicy(selectedPolicy);
    setOpenUpload(true);
  };
  const handleCloseModal = () => {
    setOpenUpload(false);
    setSelectedUploadPolicy(null);
  };
  const handleOpenEditModal = (selectedPolicy: Policy) => {
    setSelectedUploadPolicy(selectedPolicy);
    setOpenEditUpload(true);
  };
  const handleCloseEditModal = () => {
    setOpenEditUpload(false);
    setSelectedUploadPolicy(null);
    setIsConfirmEditAndDelete(false);
  };
  const handleOpenNotesIssuesModal = (selectedPolicy: Policy) => {
    setSelectedPolicy(selectedPolicy);
    setOpenNotesIssuesModal(true);
  };
  const handleOpenRevisedPolicy = (selectedPolicy: Policy) => {
    setSelectedUploadPolicy(selectedPolicy);
    setOpenNewPolicyModal(true);
  };
  const filteredPolicies = useMemo(() => {
    let result = policies;
    const assignedToIds = assignableInternalUserIds.length
      ? assignableInternalUserIds
      : assignedToId && !isUnassignedOnly
        ? [Number(assignedToId)]
        : user?.id
          ? [user.id]
          : [];
    if (isUnassignedOnly) {
      result = result.filter((p: Policy) => !hasAssignedTo(p));
    }
    if (assignedToMe) {
      if (assignedToId) {
        result = result.filter((p: Policy) => !!p.assignedToUser?.id && assignedToIds.includes(p.assignedToUser.id));
      } else {
        result = result.filter((p) => !!p.assignedToUser?.id && assignedToIds.includes(p.assignedToUser.id));
      }
    }
    if (isDefaultFilter) {
      result = result.filter((policy) => {
        const isIssueMatch = policy.issue === PolicyIssue.Unresolved;
        return isIssueMatch;
      });
    }
    if (unsentOnly) {
      result = result.filter((policy) => {
        const isStatusMatch = policy.status !== undefined && policy.status < 3; //Not Ready, Ready to Send or not uploaded;
        return isStatusMatch;
      });
    }
    return result;
  }, [
    isUnassignedOnly,
    assignedToMe,
    assignedToId,
    assignableInternalUserIds,
    policies,
    user?.id,
    managerDepartments,
    isDefaultFilter,
    unsentOnly
  ]);

  const resetDeleteModal = () => {
    setRowToDelete(null);
    setDeleteErrorMessage("");
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    if (rowToDelete) {
      const data = await fetchPolicyInfoByOrderNumber(rowToDelete.orderNumber);
      if (data.some((item) => item.guid === rowToDelete.uniqueIdentifier)) {
        setDeleteErrorMessage(
          "Policy must be removed in Select prior to deleting here. Please try again once removed in Select."
        );
      } else {
        setTableData((prev) => prev.filter((row) => row.id !== rowToDelete.id));
        await axios.post(`/api/OutstandingChecks/DeletePolicy/${rowToDelete.id}`);
        resetDeleteModal();
      }
    }
    setIsDeleting(false);
  };
  const handleCheckForPolicyNumber = async () => {
    const data = await fetchPolicyInfoByOrderNumber(selectedUploadPolicy?.orderNumber);
    const existingPolicy = data.find((item) => item.guid === selectedUploadPolicy?.uniqueIdentifier);
    if (
      !existingPolicy?.policyNumber ||
      existingPolicy?.policyNumber?.length < 6 ||
      existingPolicy?.policyNumber === "PRO FORMA"
    ) {
      setPolicyNumberCheck("Policy number needs to be added in Select to upload the Policy.");
      return false;
    }
    return true;
  };
  useEffect(() => {
    getAndSetAssignedToUser();
  }, [assignedToId]);

  useEffect(() => {
    getAndSetAssignableInternalUserIds();
  }, [assignedToId, user?.id]);

  useEffect(() => {
    if (user?.email) {
      getAndSetManagerDepartmentsByEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    setTableData(filteredPolicies);
  }, [filteredPolicies]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };

  const onSaveUpload = async (documentIds: number[], recordedDocIds?: number[]) => {
    const documentId = documentIds[0];
    if (!documentId && recordedDocIds?.length === 0) return;
    if (recordedDocIds && recordedDocIds.length > 0) {
      await axios.post("/api/outstandingChecks/AddAssociatedDocuments", {
        policyId: selectedUploadPolicy?.id,
        documentIds: recordedDocIds
      });
    }
    if (documentId) {
      await axios.post("/api/outstandingChecks/AttachPolicyDocument", {
        documentId,
        policyId: selectedUploadPolicy?.id
      });
    }
    fetchPolicies();
  };

  const editUploadedDoc = async (base64: string) => {
    const payload = {
      base64,
      documentId: selectedUploadPolicy?.documentId
    };
    if (isConfirmEditAndDelete) {
      await axios.post("/api/outstandingChecks/UpdateDocument", payload);
    }
  };
  const setPolicyNote = async (note: string) => {
    if (selectedUploadPolicy) {
      const updatedPolicy = { ...selectedUploadPolicy, note };
      setSelectedUploadPolicy(updatedPolicy);
      await axios.post("/api/outstandingChecks/UpdatePolicy", updatedPolicy);
    }
  };

  useEffect(() => {
    if (!orderNumberFromUrl) return;
    setColumnFilters([{ id: "orderNumber", value: orderNumberFromUrl }]);
    setIsDefaultFilter(false);
    setUnsentOnly(false);
    setAssignedToMe(false);
  }, [orderNumberFromUrl]);

  const isUsersShowEditPolicyIssues = Boolean(
    user?.roles?.some(
      (role) => {
        const teamName = role.internalUserTeam?.name?.toLowerCase();
        const departmentName = role.internalUserDepartment?.name?.toLowerCase() || "";
        const roleTypeName = role.internalUserRoleType?.name?.toLowerCase();

        return (
          (teamName === "policy" && ["national", "tx"].includes(departmentName)) ||
          (teamName === "development" && roleTypeName === "developer")
        );
      }
    )
  );
  const columns = getColumns(
    handleOpenModal,
    setSelectedUploadPolicy,
    handleOpenNotesIssuesModal,
    isUsersShowEditPolicyIssues,
    handleOpenRevisedPolicy,
    handleOpenEditModal,
    setPolicies
  );
  const isStatusForEdit = selectedUploadPolicy?.status !== 0 && selectedUploadPolicy?.status !== 3;
  const columnsWithNotes = NotesColumn<Policy>(columns, setSelectedPolicy);

  return (
    <>
      <Helmet>
        <title> Policy Issues | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h4" marginBottom={2}>
            Policy Issues
            {isUnassignedOnly && " - Unassigned"}
            {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
          </Typography>
          {isUsersShowEditPolicyIssues && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => setOpenNewPolicyModal(true)}
              size="medium"
            >
              Add Policy
            </Button>
          )}
        </Stack>
        <MaterialReactTable
          enableStickyHeader
          columns={columnsWithNotes}
          data={tableData}
          enableGlobalFilter
          enableFacetedValues
          enableColumnOrdering={enableOrdering}
          enableRowSelection
          autoResetPageIndex={false}
          enableRowActions={true}
          globalFilterFn="includesString"
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 },
            showColumnFilters: true,
            columnVisibility: {
              dept: false
            },
            sorting: [
              {
                id: "due",
                desc: false
              }
            ]
          }}
          renderRowActions={({ row }) => (
            <IconButton onClick={() => setRowToDelete(row.original)}>
              <DeleteIcon />
            </IconButton>
          )}
          muiTableBodyProps={{
            sx: {
              "& .MuiTableRow-root:hover .MuiTableCell-root": {
                backgroundColor: "inherit !important"
              }
            }
          }}
          muiTableBodyRowProps={({ row }) => {
            return {
              style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {}
            };
          }}
          onColumnFiltersChange={setColumnFilters as any}
          state={{
            showSkeletons: loading,
            columnFilters
          }}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <Box sx={{ flexGrow: 1, position: "relative" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <PolicyIssuesActions
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    assignedToMe={assignedToMe}
                    setAssignedToMe={setAssignedToMe}
                    table={table}
                    hideAssignedToMeOptions={!!assignedToId}
                    onRowsDeselected={onRowsDeselected}
                  />
                  <Box sx={{ position: "absolute", right: 0 }}>
                    <Tooltip title="Column ordering">
                      <IconButton onClick={toggleColumnOrdering}>
                        <Iconify icon="mdi:reorder-vertical" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to csv">
                      <IconButton onClick={() => exportTableToCsv(table, "export.csv")}>
                        <Iconify icon="eva:download-fill" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
              </Box>
            );
          }}
        />
      </Container>
      {!!selectedPolicy && (
        <NotesAndIssuesModal
          isOpen
          nameBeforeUpdate={selectedPolicy?.assignedToUser?.fullName || selectedPolicy?.assignedToName || ""}
          emailBeforeUpdate={selectedPolicy?.assignedToUser?.email || selectedPolicy?.assignedToEmail || ""}
          policy={selectedPolicy}
          setPolicy={setSelectedPolicy}
          setEntityTable={setPolicies}
          refetchData={() => fetchPolicies({ useLoader: false, resetPagination: false })}
          reportType={DashboardReportType.PolicyIssues}
          issueDropdown={false}
          setOpenNotesIssuesModal={setOpenNotesIssuesModal}
        />
      )}
      {openUpload && (
        <UploadModal
          orderNumber={selectedUploadPolicy?.orderNumber || ""}
          updateItems={fetchPolicies}
          validationCheck={selectedUploadPolicy ? handleCheckForPolicyNumber : undefined}
          open={openUpload}
          handleClose={handleCloseModal}
          onSave={onSaveUpload}
          error={policyNumberCheck}
          documentInfo={documentInfo}
          applicationInitiator="TitleQ.PoliciesToSend"
          isUseNameOfFile
          customHeaderText={""}
          customEditFunction={undefined}
          isConfirmEditAndDelete={false}
          setIsConfirmEditAndDelete={() => {}}
          setPolicyNote={setPolicyNote}
          isEditMode={false}
          policyNote={""}
        />
      )}
      {isStatusForEdit && (
        <UploadModal
          orderNumber={selectedUploadPolicy?.orderNumber || ""}
          updateItems={fetchPolicies}
          validationCheck={selectedUploadPolicy ? handleCheckForPolicyNumber : undefined}
          open={openEditUpload}
          handleClose={handleCloseEditModal}
          onSave={onSaveUpload}
          error={policyNumberCheck}
          documentInfo={documentInfo}
          applicationInitiator="TitleQ.PoliciesToSend"
          isUseNameOfFile
          customHeaderText={isStatusForEdit ? "Upload Replacement Policy" : ""}
          customEditFunction={isStatusForEdit && selectedUploadPolicy?.documentId !== 0 ? editUploadedDoc : undefined}
          isConfirmEditAndDelete={isConfirmEditAndDelete}
          setIsConfirmEditAndDelete={setIsConfirmEditAndDelete}
          setPolicyNote={setPolicyNote}
          policyNote={selectedUploadPolicy?.note}
          isEditMode={true}
        />
      )}
      {openNewPolicyModal && (
        <NewPolicyModal
          isOpen={openNewPolicyModal}
          setIsOpen={setOpenNewPolicyModal}
          updateData={fetchPolicies}
          orderNumber={selectedUploadPolicy?.status === 3 ? selectedUploadPolicy.orderNumber : ""}
          policyToRevise={selectedUploadPolicy}
          onCloseNewPolicyModal={() => setSelectedUploadPolicy(null)}
        />
      )}
      {openNotesIssuesModal && selectedPolicy && (
        <NotesAndIssuesModal
          isOpen={openNotesIssuesModal}
          nameBeforeUpdate={selectedPolicy?.assignedToUser?.fullName || selectedPolicy?.assignedToName || ""}
          emailBeforeUpdate={selectedPolicy?.assignedToUser?.email || selectedPolicy?.assignedToEmail || ""}
          policy={selectedPolicy}
          setPolicy={setSelectedPolicy}
          setEntityTable={setPolicies}
          refetchData={() => fetchPolicies({ useLoader: false, resetPagination: false })}
          reportType={DashboardReportType.PolicyIssues}
          issueDropdown
          setOpenNotesIssuesModal={setOpenNotesIssuesModal}
        />
      )}
      <Dialog open={!!rowToDelete} onClose={resetDeleteModal}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {deleteErrorMessage
            ? deleteErrorMessage
            : `Are you sure you want to delete policy #${rowToDelete?.policyNumber ? rowToDelete?.policyNumber : ""}?`}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDeleteModal}>Cancel</Button>
          {!deleteErrorMessage && (
            <Button
              onClick={handleDelete}
              color="error"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress color="inherit" size={20} /> : null}
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
